import React, { useState, useCallback } from "react";
import {
  Button,
  Input,
  Label,
  Text,
  Card,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Search24Regular,
  Building24Regular,
} from "@fluentui/react-icons";
import { useCreateRecord } from "../../hooks/useCreateRecord";
import { dataverseClient } from "../../services/dataverseClient";

const useStyles = makeStyles({
  form: { display: "flex", flexDirection: "column", gap: "12px", marginTop: "12px" },
  field: { display: "flex", flexDirection: "column", gap: "4px" },
  error: { color: tokens.colorStatusDangerForeground1 },
  actions: { display: "flex", gap: "8px" },
  searchRow: { display: "flex", gap: "4px", alignItems: "flex-end" },
  resultCard: {
    padding: "6px 8px",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedAccount: {
    padding: "6px 8px",
    borderColor: tokens.colorBrandStroke1,
    borderWidth: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultIcon: { display: "flex", alignItems: "center", gap: "6px" },
  resultMeta: { color: tokens.colorNeutralForeground3, fontSize: "12px" },
});

interface CreateRecordFormProps {
  prefillEmail?: string;
  prefillName?: string;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateRecordForm: React.FC<CreateRecordFormProps> = ({
  prefillEmail,
  prefillName,
  onClose,
  onCreated,
}) => {
  const styles = useStyles();
  const { createRecord, creating, error } = useCreateRecord();

  const nameParts = prefillName?.split(" ") ?? [];
  const [firstname, setFirstname] = useState(nameParts[0] ?? "");
  const [lastname, setLastname] = useState(nameParts.slice(1).join(" ") ?? "");
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [phone, setPhone] = useState("");

  // Account search state
  const [accountQuery, setAccountQuery] = useState("");
  const [accountResults, setAccountResults] = useState<any[]>([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);

  const handleAccountSearch = useCallback(async () => {
    if (!accountQuery.trim()) return;
    setSearchingAccounts(true);
    try {
      const results = await dataverseClient.searchAccounts(accountQuery.trim());
      setAccountResults(results);
    } catch {
      setAccountResults([]);
    } finally {
      setSearchingAccounts(false);
    }
  }, [accountQuery]);

  const handleAccountKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAccountSearch();
  };

  const handleSubmit = async () => {
    try {
      const data: Record<string, any> = {
        firstname,
        lastname,
        ...(email && { emailaddress1: email }),
        ...(phone && { telephone1: phone }),
      };
      if (selectedAccount) {
        data["parentcustomerid_account@odata.bind"] = `/accounts(${selectedAccount.id})`;
      }
      await createRecord("contact", data);
      onCreated();
    } catch {
      // error is surfaced via hook
    }
  };

  return (
    <div className={styles.form}>
      <Text weight="semibold">Create New Contact</Text>

      <div className={styles.field}>
        <Label>First Name</Label>
        <Input value={firstname} onChange={(_, d) => setFirstname(d.value)} />
      </div>
      <div className={styles.field}>
        <Label>Last Name</Label>
        <Input value={lastname} onChange={(_, d) => setLastname(d.value)} />
      </div>

      <div className={styles.field}>
        <Label>Email</Label>
        <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} />
      </div>

      <div className={styles.field}>
        <Label>Phone</Label>
        <Input type="tel" value={phone} onChange={(_, d) => setPhone(d.value)} />
      </div>

      <div className={styles.field}>
        <Label>Company (Account)</Label>
        {selectedAccount ? (
          <Card className={styles.selectedAccount}>
            <div className={styles.resultIcon}>
              <Building24Regular />
              <Text weight="semibold">{selectedAccount.name}</Text>
            </div>
            <Button
              size="small"
              appearance="subtle"
              onClick={() => {
                setSelectedAccount(null);
                setAccountQuery("");
                setAccountResults([]);
              }}
            >
              Change
            </Button>
          </Card>
        ) : (
          <>
            <div className={styles.searchRow}>
              <Input
                placeholder="Search for account..."
                value={accountQuery}
                onChange={(_, d) => setAccountQuery(d.value)}
                onKeyDown={handleAccountKeyDown}
                style={{ flex: 1 }}
              />
              <Button
                icon={<Search24Regular />}
                onClick={handleAccountSearch}
                disabled={searchingAccounts || !accountQuery.trim()}
              />
            </div>
            {searchingAccounts && <Spinner size="tiny" label="Searching..." />}
            {accountResults.map((a) => (
              <Card
                key={a.accountid}
                className={styles.resultCard}
                onClick={() => {
                  setSelectedAccount({ id: a.accountid, name: a.name });
                  setAccountResults([]);
                }}
              >
                <div className={styles.resultIcon}>
                  <Building24Regular />
                  <div>
                    <Text weight="semibold" size={300}>{a.name}</Text>
                    {(a.accountnumber || a.emailaddress1) && (
                      <Text size={200} className={styles.resultMeta} block>
                        {[a.accountnumber, a.emailaddress1].filter(Boolean).join(" | ")}
                      </Text>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {!searchingAccounts && accountQuery && accountResults.length === 0 && (
              <Text size={200}>No accounts found.</Text>
            )}
          </>
        )}
      </div>

      {error && <Text className={styles.error} size={200}>{error.message}</Text>}

      <div className={styles.actions}>
        <Button appearance="primary" onClick={handleSubmit} disabled={creating}>
          {creating ? "Creating..." : "Create Contact"}
        </Button>
        <Button appearance="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
};
